import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type PlaidLinkError,
  type PlaidLinkOnSuccess,
  type PlaidLinkOnSuccessMetadata,
  usePlaidLink,
} from "react-plaid-link";
import { useNavigate } from "react-router-dom";

import useQueryClient from "../../api/query";
import {
  clearPlaidOAuthState,
  isPlaidOAuthRedirect,
  loadPlaidOAuthState,
  type PlaidOAuthState,
} from "./plaidOAuthStorage";
import { savePendingMappingConnections } from "./plaidPendingMapping";

const BANK_TAB_PATH = "/credentials?tab=bank";

// Resumes Link after an OAuth bank redirects back to /plaid-oauth: restores the
// persisted token, re-inits Link with receivedRedirectUri, completes exchange/reauth.
export function PlaidOAuthReturn({
  updateSnackBar,
}: {
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;

  const [oauthState] = useState<PlaidOAuthState | null>(() =>
    loadPlaidOAuthState(),
  );
  const [isRedirect] = useState<boolean>(() => isPlaidOAuthRedirect());
  const canResume = isRedirect && !!oauthState;

  const finish = useCallback(
    (snackbar?: { severity: AlertColor; message: string }) => {
      clearPlaidOAuthState();
      if (snackbar) {
        updateSnackBar(snackbar);
      }
      navigate(BANK_TAB_PATH, { replace: true });
    },
    [navigate, updateSnackBar],
  );

  const handleSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      if (!oauthState) {
        finish();
        return;
      }
      try {
        if (oauthState.mode === "reauth" && oauthState.connectionId) {
          await queryClientRef.current.completePlaidReauth(
            oauthState.connectionId,
          );
          finish({ severity: "success", message: "Bank account reconnected." });
          return;
        }
        if (oauthState.mode === "add-accounts" && oauthState.connectionId) {
          // Hand new PENDING_MAPPING accounts to the Bank Connections tab to map.
          const connections = await queryClientRef.current.completeAddAccounts(
            oauthState.connectionId,
          );
          savePendingMappingConnections(connections);
          finish({
            severity: "success",
            message: "Accounts added — assign each to a practice.",
          });
          return;
        }
        if (oauthState.mode === "connect") {
          // No practice — persist PENDING_MAPPING accounts for the tab to map.
          const connections =
            await queryClientRef.current.exchangePlaidPublicToken(publicToken, {
              institutionId: metadata.institution?.institution_id,
              institutionName: metadata.institution?.name,
            });
          savePendingMappingConnections(connections);
          finish({
            severity: "success",
            message: "Bank connected — assign each account to a practice.",
          });
          return;
        }
        finish();
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        finish({
          severity: "error",
          message:
            status === 409
              ? "This bank account is already connected to another practice."
              : "Failed to connect bank account. Please try again.",
        });
      }
    },
    [oauthState, finish],
  );

  const handleExit = useCallback(
    (error: PlaidLinkError | null) => {
      if (error) {
        finish({
          severity: "error",
          message:
            "Bank connection didn't complete. Please try again or contact the Wieldy team.",
        });
      } else {
        finish();
      }
    },
    [finish],
  );

  const { open, ready } = usePlaidLink({
    token: canResume ? (oauthState as PlaidOAuthState).linkToken : null,
    receivedRedirectUri: window.location.href,
    onSuccess: handleSuccess,
    onExit: handleExit,
  });

  useEffect(() => {
    if (!canResume) {
      finish();
    }
  }, [canResume, finish]);

  useEffect(() => {
    if (canResume && ready) {
      open();
    }
  }, [canResume, ready, open]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" sx={{ color: "#667085" }}>
        Finishing bank connection…
      </Typography>
    </Box>
  );
}
